import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import igIcon from "../assets/img/platform/instagram.png";
import fbIcon from "../assets/img/platform/facebook.png";
import ytIcon from "../assets/img/platform/youtube.png";
import "../assets/css/InsightPreviewer.css";
import ReactMarkdown from "react-markdown";
import { Lightbulb, Timeline, TrendingUp, WarningAmber, Group } from "@mui/icons-material";

const formatFollowers = (count) => {
  if (!count) return "0";
  if (count >= 1000000) return (count / 1000000).toFixed(1) + "M";
  if (count >= 1000) return (count / 1000).toFixed(1) + "K";
  return count;
};

const parseBold = (str) => {
  if (!str) return null;
  const parts = str.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : part
  );
};

const renderInsightWithBlur = (text, index) => {
  // 1. Extract Category Name
  const categoryMatch = text.match(/\*\*(.*?):\*\*/);
  const category = categoryMatch ? categoryMatch[1].trim() : "Insight";

  // 2. Parse Description and Target Action
  let remaining = text;
  if (categoryMatch) {
    remaining = remaining.substring(categoryMatch[0].length).trim();
  }
  const parts = remaining.split("**Target Action**:");
  const description = parts[0].trim();
  const targetAction = parts.length > 1 ? parts[1].trim() : "";

  // 3. Heuristics for Color & Icons
  const lowerCat = category.toLowerCase();
  let themeColor = "#00A1A1"; // info (teal)
  let IconComponent = Lightbulb;
  
  if (lowerCat.includes("synergy") || lowerCat.includes("expansion") || lowerCat.includes("health") || lowerCat.includes("audience")) {
    themeColor = "#4CAF50"; // success (green)
    IconComponent = lowerCat.includes("audience") || lowerCat.includes("health") ? Group : TrendingUp;
  } else if (lowerCat.includes("alert") || lowerCat.includes("warning") || lowerCat.includes("recovery")) {
    themeColor = "#F44336"; // error (red)
    IconComponent = WarningAmber;
  } else if (lowerCat.includes("gap") || lowerCat.includes("momentum") || lowerCat.includes("skew") || lowerCat.includes("maintenance")) {
    themeColor = "#FF9800"; // warning (orange)
    IconComponent = Timeline;
  }

  // Convert hex to rgb for rgba usage
  const hexToRgb = (hex) => {
    let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : "26, 115, 232";
  };
  const rgbStr = hexToRgb(themeColor);

  return (
    <div key={index} className="app-hub-insight-card" style={{ borderLeftColor: themeColor }}>
      {/* Header Ribbon */}
      <div className="app-hub-insight-header">
        <div className="app-hub-icon-box" style={{ backgroundColor: themeColor, boxShadow: `0 4px 6px rgba(${rgbStr}, 0.2)` }}>
          <IconComponent style={{ color: "#fff", fontSize: "1.1rem" }} />
        </div>
        <span className="app-hub-insight-title" style={{ color: themeColor }}>{category}</span>
      </div>

      {/* Dynamic Markdown Description */}
      <div className="app-hub-insight-desc">
        <ReactMarkdown>{description}</ReactMarkdown>
      </div>

      {/* Target Action Block */}
      {targetAction && (
        <div className="app-hub-action-block" style={{ border: `1px dashed rgba(${rgbStr}, 0.3)` }}>
          <strong>Target Action: </strong>
          {index === 0 ? (
            <span className="clear-action">{targetAction}</span>
          ) : (
            <span className="blurred-action"> {targetAction} </span>
          )}
        </div>
      )}
    </div>
  );
};

const ScoutResults = ({ scoutState }) => {
  const appFrontendUrl = process.env.REACT_APP_URL || "https://app.comparedly.com";
  const { loading, datasets = [], insights = [], error, status, progress } = scoutState || {};
  const hasResults = !loading && insights.length > 0;
  const isActive = loading || hasResults || error;

  return (
    <AnimatePresence>
      {isActive && (
        <motion.section
          id="scout-results"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          style={{
            overflow: "hidden",
            background: "linear-gradient(135deg, #f0fdfd 0%, #e0f7fa 100%)",
            textAlign: "center"
          }}
        >
          <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "60px 20px" }}>
            {/* Loading State */}
            {loading && (
              <div className="scouting-overlay" style={{ background: "transparent", padding: "20px 0" }}>
                <div className="scouting-status">{status || "Analyzing..."}</div>
                <div style={{ width: "100%", maxWidth: "500px", margin: "0 auto", height: "8px", background: "rgba(0,161,161,0.15)", borderRadius: "4px", overflow: "hidden", marginTop: "10px" }}>
                  <div style={{ width: `${progress || 5}%`, height: "100%", background: "#FFB347", transition: "width 0.5s ease-out" }} />
                </div>
                <div style={{ marginTop: "12px", color: "#00A1A1", fontSize: "1rem", fontWeight: "800", fontFamily: "'Poppins', sans-serif" }}>
                  {progress || 5}% Complete
                </div>
              </div>
            )}

            {/* Results State */}
            {hasResults && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                {/* Brand Comparison Header */}
                <div className="vs-container" style={{ marginBottom: "40px" }}>
                  {datasets.map((brand, idx) => (
                    <React.Fragment key={idx}>
                      <div className="scouted-brand">
                        <div className="scouted-avatar-outer">
                          <div className="scouted-avatar-wrapper">
                            {brand.picture ? (
                              <img
                                src={brand.picture}
                                className="scouted-avatar"
                                alt={brand.brandName}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div
                              className="scouted-avatar-placeholder"
                              style={{ display: brand.picture ? 'none' : 'flex' }}
                            >
                              {brand.brandName?.charAt(0)}
                            </div>
                          </div>
                          <div className="platform-badge-floating">
                            <img
                              src={brand.platform === "Instagram" ? igIcon : brand.platform === "Facebook" ? fbIcon : ytIcon}
                              alt={brand.platform}
                            />
                          </div>
                        </div>
                        <div className="scouted-name">{brand.brandName}</div>
                        <div className="scouted-followers">
                          {formatFollowers(brand.audience?.end || 0)} {brand.platform === "YouTube" ? "Subscribers" : "Followers"}
                        </div>
                      </div>
                      {idx === 0 && <div className="vs-badge">VS</div>}
                    </React.Fragment>
                  ))}
                </div>

                {/* Insights */}
                <div className="result-card">
                  {insights.map((text, idx) => renderInsightWithBlur(text, idx))}
                </div>

                {/* CTA after results */}
                <div className="cta-banner" style={{ marginTop: "40px" }}>
                  <h3>Ready to see the full strategy?</h3>
                  <p>Join brands using Comparedly to dominate the market landscape.</p>
                  <a href={`${appFrontendUrl}/auth/register`} className="cta-button">
                    Unlock Full Strategic Roadmap — Free
                  </a>
                </div>
              </motion.div>
            )}

            {/* Error State */}
            {!loading && error && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <p style={{ color: "#ef4444", fontWeight: "700", fontSize: "1.1rem", marginBottom: "24px", fontFamily: "'Poppins', sans-serif" }}>{error}</p>
              </motion.div>
            )}
          </div>
        </motion.section>
      )}
    </AnimatePresence>
  );
};

export default ScoutResults;
